import moment from 'moment/moment';

import { EmpBasicData, EmpEmeContact, EmpHomeContact } from '~/data/employee';
import { OverlappingEmpForm } from '~/features/OverlappingEmploymentDialog/hooks/useOverlappingEmploymentForm';
import { fetchFromOldApi } from '~/services/Http';

const transformBasicPayload = (
  empBasicData: EmpBasicData,
  form: OverlappingEmpForm,
  empEmeContact: EmpEmeContact,
  empHomeContact: EmpHomeContact,
  isHrOrRecruiter: boolean,
) => {
  const {
    fEmpProfileId,
    fEmpFirstName,
    fEmpGivenNames,
    fEmpLastName,
    fEmpDateOfBirth,
    fEmpSocSecNum,
    fEmpNationality,
    fEmpPersonNumber,
    fEmpGender,
  } = empBasicData;
  const {
    fEmpLegalEntity,
    fInternalPositionPosition,
    fEmpJobTitle,
    fEmpBusinessUnit1,
    fInternalPositionManager,
    fEmplDetailStatus,
    fEmplDateStart,
    fEmpCostCenter,
    fEmpAddlCostCenters,
    fEmplType,
    fEmplCurrency,
    fEmplDateArrival,
    fEmplTrialPeriod,
    fEmplTrialPeriodEndDate,
    fEmplLastDateEmployment,
    fEmplTerminationDate,
    fEmplTerminationReason,
    fEmplFixedTermReason,
    fEmplPlannedEndDate,
  } = form.overlappingEmployment.basicData;
  const {
    fEmplInfo,
    fEmplDetailJobGrade,
    fEmplDetailTaxNumber,
    fEmplDetailCalcHoursBalance,
    fEmplDetailVacBalCalcSixDays,
    fEmplDetailPersonnelGroup1,
    fEmplDetailPersonnelGroup2,
    fEmplDetailPersonnelGroup3,
    fEmplDetailPersonnelGroup4,
    fEmplDetailPersonnelGroup5,
  } = form.overlappingEmployment.employmentDetails;

  return [
    {
      empData: {
        empBasicData: {
          fEmpProfileId,
          fEmpFirstName,
          fEmpGivenNames,
          fEmpLastName,
          fEmpDateOfBirth,
          fEmpSocSecNum,
          fEmpNationality,
          fEmpPersonNumber,
          fEmpGender,
        },
        empHomeContact,
        empEmeContact,
        empWorkContact: form.overlappingEmployment.contact,
        empPosition: {
          fInternalPositionPosition,
          fEmpJobTitle,
          fEmpLegalEntity,
          fInternalPositionUnit: fEmpBusinessUnit1,
          fInternalPositionManager,
          fInternalPositionIsCurrent: true,
          fInternalPositionInfo: '',
          fInternalPositionStartDate: fEmplDateStart,
          fEmpCostCenters: [
            {
              fEmpCostCenterIsDefault: true,
              fEmpCostCenter: +fEmpCostCenter,
            },
            ...(fEmpAddlCostCenters ?? []),
          ],
        },
      },
      employment: {
        fEmplType,
        fEmplCurrency,
        fEmplDateStart,
        fEmplDateArrival,
        fEmplTrialPeriod,
        fEmplTrialPeriodEndDate,
        fEmplLastDateEmployment,
        fEmplTerminationDate,
        fEmplTerminationReason,
        fEmplFixedTermReason,
        fEmplPlannedEndDate,
        fEmplInfo,
      },
      employmentDetails: {
        fEmplDetailJobGrade,
        fEmplDetailStatus,
        fEmplDetailStartDate: fEmplDateStart,
        fEmplDetailTaxNumber,
        fEmplDetailIsPending: !(
          moment(fEmplDateStart).isSameOrBefore(moment().format()) &&
          isHrOrRecruiter
        ),
        fEmplDetailCalcHoursBalance,
        fEmplDetailVacBalCalcSixDays,
        fEmplDetailPersonnelGroup1,
        fEmplDetailPersonnelGroup2,
        fEmplDetailPersonnelGroup3,
        fEmplDetailPersonnelGroup4,
        fEmplDetailPersonnelGroup5,
      },
    },
  ];
};

export const saveBasicData = async (
  form: OverlappingEmpForm,
  empBasicData: EmpBasicData,
  empEmeContact: EmpEmeContact,
  empHomeContact: EmpHomeContact,
  isHrOrRecruiter: boolean,
  onAfterSubmit: (success: boolean) => void,
) => {
  const payload = transformBasicPayload(
    empBasicData,
    form,
    empEmeContact,
    empHomeContact,
    isHrOrRecruiter,
  );
  const api = `/json/employee/secondary`;
  return await fetchFromOldApi(api, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
    .then((r) => r.json())
    .then((data) => {
      onAfterSubmit(true);
      return data;
    })
    .catch((e) => {
      onAfterSubmit(false);
      console.log('saveBasicData', e);
    });
};
